import React, { useEffect } from "react";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useOutletContext, useParams } from "react-router-dom";

import AlertContainer from "../../components/common/AlertContainer";
import SpinningCircle from "../../components/common/SpinningCircle";
import { Password } from "../../components/controls/Password";
import { SubmitButton } from "../../components/controls/SubmitButton";
import {
	REGISTER_B2B_URL,
	REGISTER_URL,
	TYPE_DEBOUNCE,
} from "../settings/common.utils";
import { trackEvent } from "../../utils/tracking";
import { TRACKING_EVENTS } from "../../utils/trackingDefinitions";

import { useDispatch, useSelector } from "../../hooks";

import {
	handleFieldValueChange,
	onAlertClose,
	triggerAlert,
} from "./slices/password.slice";
import { resetPassword } from "./slices/password.actions";
import { useDebouncedCallback } from "use-debounce";
import { OutletContext } from "./slices/password.types";

export const ResetPasswordForm = () => {
	const context = useOutletContext<OutletContext>();
	const { formatMessage } = useIntl();
	const { nonce } = useParams();
	const dispatch = useDispatch();

	const schema = yup.object({
		password: yup
			.string()
			.min(8, "komid.register.validation.required")
			.max(70, "komid.register.validation.length.max"),
	});

	const {
		setValue,
		register,
		handleSubmit,
		trigger,
		clearErrors,
		formState: { errors },
	} = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
		resolver: yupResolver(schema),
	});

	const returnUrl = useSelector((state) => state.login.form.returnUrl);
	const alerts = useSelector((state) => state.password.alerts);
	const isResetingPassword = useSelector(
		(state) => state.password.isResetingPassword,
	);
	const username = useSelector((state) => state.password.form.email);
	const isStoreB2B = useSelector((state) => state.settings.isStoreB2B);

	const handleDebounedFieldChange = useDebouncedCallback(async (payload) => {
		const isValid = await trigger(payload.field);

		if (isValid) {
			clearErrors(payload.field);
		}

		dispatch(handleFieldValueChange(payload));
	}, TYPE_DEBOUNCE);

	const onFieldValueChange = async (event) => {
		const { id, value } = event.target;

		setValue(id, value);

		await handleDebounedFieldChange({
			field: id,
			value,
		});
	};

	const handleOnRegisterLinkClick = (e) => {
		e.preventDefault();

		trackEvent({
			event: TRACKING_EVENTS.LOGIN.EVENT.USER_INTERACTION,
			target: TRACKING_EVENTS.PASSWORD.APP,
			key: "action",
			value: TRACKING_EVENTS.LOGIN.EVENT.REGISTER_LINK_CLICK,
		});

		const url = new URL(
			`${window.location.origin}${
				isStoreB2B ? REGISTER_B2B_URL : REGISTER_URL
			}`,
		);

		url.searchParams.append("returnUrl", returnUrl);

		window.location.href = url.href;
	};

	const onSubmit = handleSubmit(async (data) => {
		dispatch(onAlertClose([]));

		const isValid = await trigger(["password"]);

		if (isValid) {
			dispatch(
				resetPassword({
					username,
					nonce,
					password: data.password,
				}),
			);
		}
	});

	useEffect(() => {
		if (!context.isValid) {
			dispatch(
				triggerAlert([
					{
						level: "error",
						messageKey: "komid.resetpassword.expired.null.message",
					},
				]),
			);
		}
	}, [context.isValid, dispatch]);

	return (
		<>
			<AlertContainer
				alerts={alerts}
				onAlertClose={(data) => dispatch(onAlertClose(data))}
			/>

			<div className="form-wrapper">
				{context.isValid && (
					<section className="form-section">
						<h1>{formatMessage({ id: "komid.resetpassword.header" })}</h1>
						<h3>
							{formatMessage({ id: "komid.login.form.new.user" })}{" "}
							<a
								href="#"
								data-testid="reset.password.form.register.user.link"
								onClick={handleOnRegisterLinkClick}
							>
								{formatMessage({ id: "komid.login.form.new.user.link" })}
							</a>
						</h3>
						<form data-hj-suppress onSubmit={onSubmit}>
							<fieldset>
								<Password
									id="password"
									ref={register("password")?.ref}
									error={
										typeof errors.password?.message === "string"
											? errors.password.message
											: undefined
									}
									className="form-input"
									label={formatMessage({
										id: "komid.resetpassword.password.mincharacters",
									})}
									onChange={(event) => {
										onFieldValueChange(event);
									}}
									placeholder="******"
									maxLength={50}
									autoComplete="current-password"
									showValue
								/>

								<div className="form-buttons">
									<SubmitButton
										onClick={onSubmit}
										isFetching={isResetingPassword}
										data-automationid="resetPassword.submitButton"
										data-testid="resetPassword.submitButton"
									>
										{formatMessage({ id: "komid.resetpassword.submit" })}
										{isResetingPassword && (
											<SpinningCircle className="loader" />
										)}
									</SubmitButton>
								</div>
							</fieldset>
						</form>
						<div className="form-bottom-links">
							<p>{formatMessage({ id: "komid.login.form.forgot.password" })}</p>
							<a
								data-testid="reset.password.form.passwordless.login.link"
								href={`/password/loginlink?returnUrl=${encodeURIComponent(
									returnUrl,
								)}`}
							>
								{formatMessage({ id: "komid.login.form.forgot.password.link" })}
							</a>
						</div>
					</section>
				)}
			</div>
		</>
	);
};
